import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

// Import helpers
import { openNotification } from 'components/utilities/notification/notification';
import { AUTH_METHODS, fetchUserLocalization, APPLE_AUTH_PROVIDER, APPLE_AUTH_PROVIDER_ID } from '../helpers';

// Import context
import { AuthenticationContext } from 'components/context/authentication/AuthenticationProvider';
import { POST_SUBSCRIBER_LOGIN_EXTERNAL_API } from 'helpers/api';

// Import utilities
import { useAuthorization, useOperators } from 'components/utilities';

const { REACT_APP_FIREBASE_AUTH } = process.env;
const { PHONE } = AUTH_METHODS;

const useAuthentication = () => {
	// Get loader handle function from App context
	const { handleLoader } = useContext(AuthenticationContext);
	const { isOperator } = useOperators();

	const { t } = useTranslation();
	const { userAuthorization } = useAuthorization({
		apiUrl: POST_SUBSCRIBER_LOGIN_EXTERNAL_API,
		multiAccount: isOperator
	});

	// state
	const [isPhoneAuthView, setIsPhoneAuthView] = useState(false);
	const [userCountryCode, setUserCountryCode] = useState(null);

	// variables
	const showPhoneAuthentication = REACT_APP_FIREBASE_AUTH.includes(PHONE);

	const handleAuthenticationSubmit = async (authProvider) => {
		try {
			// set loading
			handleLoader(true);

			// init authentication
			const AuthProvider = firebase.auth[authProvider];
			const provider = authProvider === APPLE_AUTH_PROVIDER ? new AuthProvider(APPLE_AUTH_PROVIDER_ID) : new AuthProvider();
			const { providerId } = provider;

			// get user id token
			const { user } = await firebase.auth().signInWithPopup(provider);
			const idToken = await user.getIdToken();

			// dispatch user authorization
			await userAuthorization({ idToken, providerId });

			// turn off loading
			handleLoader(false);
		} catch (error) {
			// turn off loading
			handleLoader(false);

			// open notification
			openNotification({
				type: 'error',
				title: t('common_general_error'),
				description: t('common_login_firebase_error')
			});
		}
	};

	const handleSetIsPhoneAuthView = () => setIsPhoneAuthView(true);

	const handleHidePhoneAuthView = () => setIsPhoneAuthView(false);

	const handleFetchUserLocalization = async () => {
		if (showPhoneAuthentication) {
			const { countryCode } = await fetchUserLocalization();
			countryCode && setUserCountryCode(countryCode.toLowerCase());
		}
	};

	useEffect(() => {
		handleFetchUserLocalization();
		// eslint-disable-next-line
	}, [showPhoneAuthentication]);

	return {
		isPhoneAuthView,
		showPhoneAuthentication,
		userCountryCode,
		authenticationSubmit: handleAuthenticationSubmit,
		setIsPhoneAuthView: handleSetIsPhoneAuthView,
		hidePhoneAuthView: handleHidePhoneAuthView
	};
};

export default useAuthentication;
